import React from "react";
import { NMFormPopup } from "@neumetric/blueprint-lib/components";
import { useSelector, useDispatch } from "react-redux";
import { getDepartments, getActiveUsers } from "@neumetric/blueprint-lib/store";
import {
  getThreatsList,
  getVulnerabilitiesList,
  getAssetsList,
  getSeverityByScore,
  getSeverityList,
  riskActions,
  getDepartmentResponders,
} from "../../store";

const NewRiskFormFields = [
  {
    label: "Title",
    required: true,
    attribute: "title",
    typeRepresentation: "TEXT",
    placeholder: "Provide a short title for the risk",
    classes: { root: "w-100", label: "w-100-px" },
  },
  {
    label: "Department",
    required: true,
    attribute: "department",
    typeRepresentation: "DROPDOWN",
    placeholder: "Department",
    classes: { root: "w-333-px", label: "w-100-px" },
  },
  {
    label: "Asset",
    required: true,
    attribute: "asset_id",
    typeRepresentation: "DROPDOWN",
    placeholder: "Asset",
    searchableFlag: true,
    classes: { root: "w-390-px", label: "w-103-px" },
  },
  {
    label: "Responder",
    required: true,
    attribute: "responder",
    typeRepresentation: "DROPDOWN",
    placeholder: "Responders",
    searchableFlag: true,
    template: "USER",
    labelTemplate: "LIST",
    classes: { root: "w-400-px", label: "w-100-px" },
  },
  {
    label: "Threat",
    required: true,
    attribute: "threat_id",
    typeRepresentation: "DROPDOWN",
    placeholder: "Threat",
    searchableFlag: true,
    classes: { root: "w-390-px", label: "w-103-px" },
  },
  {
    label: "Stackholder",
    required: false,
    attribute: "stakeholder_id",
    typeRepresentation: "DROPDOWN",
    placeholder: "StackHolder",
    searchableFlag: true,
    template: "USER",
    labelTemplate: "LIST",
    classes: { root: "w-400-px", label: "w-100-px" },
  },
  {
    label: "Vulnerability",
    required: true,
    attribute: "vulnerability_id",
    typeRepresentation: "DROPDOWN",
    placeholder: "Vulnerability",
    searchableFlag: true,
    classes: { root: "w-390-px", label: "w-103-px" },
  },
  {
    label: "Context",
    required: true,
    attribute: "context",
    typeRepresentation: "TEXT",
    placeholder: "Provide a short title for the risk",
    classes: { root: "w-100", label: "w-100-px" },
  },
  {
    label: "Consequence",
    typeRepresentation: "TEXTAREA",
    attribute: "consequence",
    rows: 3,
    placeholder: "Provide a short title for the risk",
    classes: { root: "w-100", label: "w-100-px" },
  },
  {
    label: "Rating",
    attribute: "rating",
    required: true,
    layout: "horizontal",
    typeRepresentation: "SCORE_GROUP",
    groups: [
      { label: "Confidentiality:", required: true, attribute: "confidentiality", classes: { label: "w-170-px" } },
      { label: "Integrity:", required: true, attribute: "integrity", classes: { label: "w-170-px" } },
      { label: "Availability:", required: true, attribute: "availability", classes: { label: "w-170-px" } },
      { label: "Security:", required: true, attribute: "security", classes: { label: "w-170-px" } },
      { label: "Privacy:", required: true, attribute: "privacy", classes: { label: "w-170-px" } },
    ],
    classes: { root: "w-430-px mb-0", label: "w-100-px" },
  },
  {
    className: "w-370-px flex-wrap",
    childrenFields: [
      {
        label: "Impact",
        layout: "vertical",
        attribute: "impact",
        typeRepresentation: "SCORE",
        readOnly: true,
        classes: { root: "w-131-px mr-30 mb-10", label: "fs-18 fw-600" },
      },
      {
        label: "Score",
        layout: "vertical",
        attribute: "score",
        typeRepresentation: "SCORE",
        readOnly: true,
        type: "PROGRESS",
        classes: { root: "w-180-px mb-10", label: "fs-22 fw-600" },
      },
      {
        label: "Probability",
        attribute: "probability",
        layout: "vertical",
        required: true,
        typeRepresentation: "SCORE",
        classes: { root: "w-131-px", label: "fs-18 fw-600" },
      },
    ],
  },
];

export const CreateRiskDialog = (props) => {
  const _formRef = React.useRef(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const dispatch = useDispatch();
  const departments = useSelector(getDepartments);
  const threatsList = useSelector(getThreatsList);
  const activeUsers = useSelector(getActiveUsers);
  const vulnerabilitiesList = useSelector(getVulnerabilitiesList);
  const assetsList = useSelector(getAssetsList);
  const severities = useSelector(getSeverityList);
  const deptResponders = useSelector(getDepartmentResponders);

  const handleCancel = () => {
    props.onClose();
  };

  const handleConfirm = () => {
    if (!_formRef.current.validate()) {
      return;
    }
    let payload = _formRef.current.getValues();
    payload = { ...payload, ...payload.rating };
    delete payload.rating;
    payload.decision = payload.score <= 3 ? "acceptable" : "treat";
    payload.risk_status = payload.score <= 3 ? "accepted" : "pending";
    payload.severity = getSeverityByScore(severities, payload.score);
    payload.status = payload.risk_status === "accepted" ? "accepted" : "draft";
    payload.risk_responders_attributes = [{ ent_usr_id: payload.responder }];
    if (payload.decision === "acceptable") {
      payload.residual_impact = payload.impact;
      payload.residual_score = payload.score;
      payload.residual_probability = payload.probability;
    } else if (payload.decision === "treat") {
      payload.residual_probability = payload.probability;
    }
    const threat = threatsList.find((threat) => threat.id === payload.threat_id);
    const vulnerability = vulnerabilitiesList.find((vulnerability) => vulnerability.id === payload.vulnerability_id);
    const asset = assetsList.find((asset) => asset.id === payload.asset_id);
    if (asset) {
      payload.asset_type_id = asset.asset_type_id;
    }
    if (threat) {
      payload.source_id = threat.source_id;
    }
    if (vulnerability) {
      payload.category = vulnerability.category;
    }
    delete payload.responder;
    const action = riskActions.createRisk(payload);
    action.onComplete = () => {
      setIsSubmitting(false);
      props.onClose(null, true);
    };
    action.onFailure = () => {
      setIsSubmitting(false);
    };
    dispatch(action);
  };

  const getOptions = (attribute) => {
    if (attribute === "department") {
      return departments;
    }
    if (attribute === "threat_id") {
      return threatsList;
    }
    if (attribute === "responder" || attribute === "stakeholder_id") {
      return activeUsers;
    }
    if (attribute === "vulnerability_id") {
      return vulnerabilitiesList;
    }
    if (attribute === "asset_id") {
      return assetsList;
    }
    return [];
  };

  const handleChange = (attribute, value) => {
    const values = _formRef.current.getValues();
    const newValues = {};
    if (attribute === "rating") {
      let impact = Object.values(value || {}).reduce((a, b) => {
        if (b > -1) {
          return a + b;
        }
        return a;
      }, 0);
      if (impact > -1) {
        impact = Math.round(impact / 5);
        let score = values.score;
        if (values.probability > -1) {
          score = Math.round((impact * values.probability) / 5);
        }
        newValues.impact = impact;
        newValues.score = score;
      }
    }
    if (attribute === "probability") {
      const score = Math.round((values.impact * value) / 5);
      if (score > -1) {
        newValues.score = score;
      }
    }
    if (attribute === "department") {
      const deptResponder = deptResponders.find((dept) => dept.department_name.toLowerCase() === value.toLowerCase());
      if (deptResponder && deptResponder.responder) {
        newValues.responder = deptResponder.responder.id;
      }
    }
    if (Object.keys(newValues).length > 0) {
      _formRef.current.updateFormData(newValues);
    }
  };

  return (
    <NMFormPopup
      ref={_formRef}
      width={850}
      variant="INFO"
      visible={true}
      title="Create New Risk"
      isSubmitting={isSubmitting}
      fields={NewRiskFormFields}
      onClose={handleCancel}
      getOptions={getOptions}
      onChange={handleChange}
      formData={{}}
      actions={[
        {
          label: "Cancel",
          icon: "CANCEL",
          btnType: "TEXT",
          onClick: handleCancel,
        },
        {
          label: "Confirm",
          icon: "CHECK_CONFIRM",
          type: "submit",
          onClick: handleConfirm,
        },
      ]}
    />
  );
};
